import { Package, Megaphone, Home, Settings, Brain, Database, LayoutDashboard, DollarSign, Target, Wrench, Phone, Mail, TrendingUp, Star, Gauge, MessageSquare, FileText, Briefcase, Banknote, Car, Shield, LogOut } from "lucide-react";
import { signOut } from "@/lib/auth";
import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

const executiveItems = [
  { title: "Management Overview", url: "/management", icon: Briefcase, slug: "management" },
  { title: "Pro Dashboard", url: "/pro-dashboard", icon: Gauge, slug: "pro-dashboard" },
  { title: "Executive Summary", url: "/executive-summary", icon: Star, slug: "executive-summary" },
  { title: "Business Hub", url: "/business-hub", icon: LayoutDashboard, slug: "business-hub" },
];

const reportingItems = [
  { title: "Financial Hub", url: "/financial-hub", icon: Banknote, slug: "financial-hub" },
  { title: "Marketing Intelligence", url: "/marketing-intelligence", icon: Target, slug: "marketing-intelligence" },
  { title: "Operations Centre", url: "/operations-centre", icon: Wrench, slug: "operations-centre" },
  { title: "Customer Service", url: "/customer-service", icon: Phone, slug: "customer-service" },
  { title: "Weekly Phone Report", url: "/reports/weekly-phone", icon: Phone, slug: "weekly-phone-report" },
  { title: "Vehicle Usage", url: "/vehicle-usage", icon: Car, slug: "vehicle-usage" },
  { title: "Report Distribution", url: "/report-distribution", icon: Mail, slug: "report-distribution" },
];

const analyticsItems = [
  { title: "Overview", url: "/", icon: Home, slug: null },
  { title: "Chat", url: "/chat", icon: MessageSquare, slug: null },
  { title: "Reports", url: "/reports", icon: FileText, slug: null },
  { title: "Campaigns", url: "/campaigns", icon: Megaphone, slug: null },
  { title: "AI Analysis", url: "/analysis", icon: Brain, slug: null },
  { title: "Products", url: "/products", icon: Package, slug: null },
];

const systemItems = [
  { title: "Schema Explorer", url: "/schema", icon: Database, slug: null },
  { title: "Settings", url: "/settings", icon: Settings, slug: null },
];

interface AppSidebarProps {
  permittedSlugs?: string[];
  isAdmin?: boolean;
}

export function AppSidebar({ permittedSlugs = [], isAdmin = false }: AppSidebarProps) {
  const [location] = useLocation();
  const allowed = new Set(permittedSlugs);
  const filterByAccess = <T extends { slug: string | null }>(items: T[]) =>
    items.filter((i) => !i.slug || isAdmin || allowed.has(i.slug));
  const exec = filterByAccess(executiveItems);
  const reps = filterByAccess(reportingItems);

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border px-4 py-5">
        <div className="flex flex-col items-start gap-2">
          <img
            src="/aquila-logo.svg"
            alt="Aquila Heating & Plumbing"
            className="h-12 w-auto max-w-full object-contain object-left dark:hidden"
          />
          <img
            src="/aquila-logo-white.svg"
            alt="Aquila Heating & Plumbing"
            className="h-12 w-auto max-w-full object-contain object-left hidden dark:block"
          />
          <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-muted-foreground/80 pt-0.5">
            Reporting Engine
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {exec.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel>Executive</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {exec.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                  >
                    <Link href={item.url} data-testid={`nav-${item.title.toLowerCase().replace(/ /g, "-")}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
        {reps.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel>Reporting Suites</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {reps.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                  >
                    <Link href={item.url} data-testid={`nav-${item.title.toLowerCase().replace(/ /g, "-")}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
        {isAdmin && (
        <SidebarGroup>
          <SidebarGroupLabel>Analytics</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {analyticsItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                  >
                    <Link href={item.url} data-testid={`nav-${item.title.toLowerCase().replace(/ /g, "-")}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
        {isAdmin && (
        <SidebarGroup>
          <SidebarGroupLabel>System</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {systemItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                  >
                    <Link href={item.url} data-testid={`nav-${item.title.toLowerCase().replace(/ /g, "-")}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location === "/admin"}>
                  <Link href="/admin" data-testid="nav-admin">
                    <Shield className="h-4 w-4" />
                    <span>Admin</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={() => signOut()} data-testid="nav-signout">
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}